import React from 'react'
import { Grid, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import { PrimitiveTableColumn, TableFilter } from '../../../types'
import InfiniteLoaderList from '../InfiniteLoaderList'
import NoResultsForFiltersLabel from './filters/NoResultsForFiltersLabel'
import PrimitiveTableHeader, {
  PrimitiveTableHeaderProps,
} from './PrimitiveTableHeader'
import PrimitiveTableRow, { PrimitiveTableRowProps } from './PrimitiveTableRow'

const useStyles = makeStyles(
  () => ({
    mainContainer: {
      height: '100%',
    },
  }),
  { name: 'PrimitiveTable' },
)

const getColumnsTotalWidth = (columns: PrimitiveTableColumn[]) => {
  const width = columns.reduce((acc, column) => acc + column.width, 0)

  return width || 6
}

const defaultGetUniqueId = R.prop('id')

export interface PrimitiveTableProps<T = any> {
  readonly HeaderProps?: Partial<PrimitiveTableHeaderProps>
  readonly RowComponent?: React.JSXElementConstructor<any>
  readonly RowComponentProps?: any
  readonly columns: PrimitiveTableColumn[]
  readonly expandable?: boolean
  readonly filters?: Record<string, TableFilter>
  readonly getDisabled?: (item: any) => boolean
  readonly getItemSize?: number | (() => number)
  readonly getRowColoring?: (item: any) => string
  readonly getUniqueId?: (item: any) => string
  readonly hasSelectedFilters?: boolean
  readonly hideHeader?: boolean
  readonly isItemLoaded: (index: number) => boolean
  readonly isItemSelected?: (item: T) => boolean
  readonly isLoading?: boolean
  readonly isSelectMode?: boolean
  readonly itemSpacing?: number
  readonly list?: T[]
  readonly loadMoreItems: (startIndex: number, endIndex: number) => void
  readonly mainColumnCount?: number
  readonly onApplyFilter?: (filter: string, value: TableFilter) => void
  readonly onApplySorting?: (sort: string, value: boolean) => void
  readonly onClearFilters?: () => void
  readonly onClearSortingItem?: (sort: string) => void
  readonly onItemClick?: (item: T) => void
  readonly onItemDelete?: (id: string) => void
  readonly onItemDuplicate?: (id: string) => void
  readonly onItemsRendered?: () => void
  readonly onSelectionChange?: (item: T, selection: boolean) => void
  readonly rowClasses?: PrimitiveTableRowProps['data']['classes']
  readonly searchTerm?: string
  readonly selectedItemId?: string
  readonly sortings?: Record<string, boolean>
  readonly totalCount: number
  readonly useWindowScroll?: boolean
}

const PrimitiveTable = ({
  HeaderProps: HeaderPropsProp,
  RowComponent,
  RowComponentProps: RowComponentPropsProp,
  columns,
  expandable = true,
  filters,
  sortings,
  getDisabled,
  getItemSize,
  getRowColoring,
  getUniqueId = defaultGetUniqueId,
  hasSelectedFilters,
  hideHeader = false,
  isItemLoaded,
  isLoading,
  itemSpacing,
  list: listProp,
  loadMoreItems: loadMoreItemsProp,
  mainColumnCount = 1,
  onApplyFilter = R.F,
  onApplySorting = R.F,
  onClearFilters = R.F,
  onClearSortingItem = R.F,
  onItemClick,
  onItemDelete,
  onItemDuplicate,
  onItemsRendered,
  rowClasses: rowClassesProp,
  searchTerm,
  selectedItemId,
  totalCount,
  useWindowScroll,
  isSelectMode,
  isItemSelected,
  onSelectionChange,
}: PrimitiveTableProps) => {
  const HeaderProps = HeaderPropsProp || {}
  const RowComponentProps = RowComponentPropsProp || {}
  const rowClasses = rowClassesProp || {}
  const list = listProp || []

  const classes = useStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const hasScrollSpace = !useWindowScroll
  const hasSelectedItem = !R.isNil(selectedItemId)
  const mainColumns = columns.slice(0, mainColumnCount)
  const otherColumns = columns.slice(mainColumnCount)
  const mainColumnWidth = expandable
    ? hasSelectedItem
      ? 12
      : getColumnsTotalWidth(mainColumns)
    : getColumnsTotalWidth(mainColumns)

  const loadMoreItems = (startIndex: number, endIndex: number) => {
    if (startIndex === 0) {
      return
    }
    if (loadMoreItemsProp) {
      loadMoreItemsProp(startIndex, endIndex)
    }
  }

  const data = {
    classes: rowClasses,
    expandable,
    getDisabled,
    getItemSize,
    getRowColoring,
    getUniqueId,
    isMobile,
    mainColumns,
    mainColumnWidth,
    onClick: onItemClick,
    onItemDelete,
    onItemDuplicate,
    otherColumns,
    rowFocused: selectedItemId,
    searchTerm,
    isSelectMode,
    isItemSelected,
    onSelectionChange,
  }

  return (
    <Grid
      container
      className={classes.mainContainer}
      direction="column"
      wrap="nowrap"
    >
      {!hideHeader && (
        <PrimitiveTableHeader
          expandable={expandable}
          filters={filters}
          hasScrollSpace={hasScrollSpace}
          hasSelectedItem={hasSelectedItem}
          mainColumnWidth={mainColumnWidth}
          mainColumns={mainColumns}
          otherColumns={otherColumns}
          showControlButtons={Boolean(onItemDelete) || Boolean(onItemDuplicate)}
          sortings={sortings}
          onApplyFilter={onApplyFilter}
          onApplySorting={onApplySorting}
          onClearFilters={onClearFilters}
          onClearSorting={onClearSortingItem}
          {...HeaderProps}
        />
      )}
      {hasSelectedFilters && list.length === 0 && !isLoading ? (
        <NoResultsForFiltersLabel
          filters={filters}
          onClearFilters={onClearFilters}
        />
      ) : (
        <InfiniteLoaderList
          isItemLoaded={isItemLoaded}
          itemCount={totalCount || list.length}
          itemData={list}
          itemSize={getItemSize}
          itemSpacing={itemSpacing}
          loadMoreItems={loadMoreItems}
          // Keeps the scrollbar for aligning the headings
          style={hasScrollSpace ? { overflowY: 'scroll' } : undefined}
          useWindowScroll={useWindowScroll}
          onItemsRendered={onItemsRendered}
        >
          {(item, index) =>
            RowComponent ? (
              <RowComponent
                data={data}
                index={index}
                isItemLoaded={isItemLoaded}
                item={item}
                {...RowComponentProps}
              />
            ) : (
              <PrimitiveTableRow
                data={data}
                item={item}
                {...RowComponentProps}
              />
            )
          }
        </InfiniteLoaderList>
      )}
    </Grid>
  )
}

export default PrimitiveTable
